/**
 * Created by Ella Ma on 2020/09/09.
 * ------ maintenance history ------
 * Changed by Daniel on 3/18/2021 - Added import workflow feature
 */

import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Store, createSelector, select } from '@ngrx/store';
import { of, Subscription } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';
import * as _ from 'lodash';

import { SystemUser } from '../../../tamalelibs/models/user.model';
import { DualListConfig, DualListEventType } from '../../../widgets/dual-list/dual-list.config';
import { businessConstants } from '../../../constants/business.constants';
import { WorkflowDialogService } from './workflow-dialog.service';
import { AppState } from '../../../redux';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { DualListItem } from '../../../widgets/dual-list/dual-list.component';
import { WorkflowDialogOperations } from '../workflow-model';
import { WorkflowActionTypes } from '../../../tamalelibs/redux/actions/workflow.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkflowConfig } from '../../../tamalelibs/models/workflow.model';
import { WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { SystemActionTypes } from '../../../tamalelibs/redux/actions/system.actions';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { ToastService } from './../../../widgets/toast/toast.service';
import { WorkflowConfigService } from './../../../tamalelibs/services/workflow-config.service';
import { FileHelperService } from './../../../services/file-helper.service';
import * as _lodash from 'lodash';

@Component({
    selector: 'tam-workflow-dialog',
    templateUrl: './workflow-dialog.component.html',
    styleUrls: ['./workflow-dialog.component.scss']
})
export class WorkflowDialogComponent implements OnInit, OnDestroy {

    @ViewChild('dualList', { static: false }) dualListInst;

    dualListConfig: DualListConfig;
    contentForm = new UntypedFormGroup({
        name: new UntypedFormControl(''),
        description: new UntypedFormControl('')
    });
    dialogTitle = '';
    config = new WorkflowConfig();
    dialogOpened = false;
    isSaving = false;

    get name() {
        return this.contentForm.get('name');
    }

    get description() {
        return this.contentForm.get('description');
    }

    private _destroySubscriptions: Array<Subscription> = [];
    private _opType;
    private _accessList: Array<string> = [];
    private _hadModify = false;
    private _currentUser: SystemUser;
    private _disabledCancel: boolean;

    constructor(
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _alertWindow: AlertWindowService,
        private _workflowDialogService: WorkflowDialogService,
        private _router: Router,
        private _service: WorkflowService,
        private _toast: ToastService,
        private _workflowConfigService: WorkflowConfigService,
        private _fileHelperService: FileHelperService
    ) { }

    ngOnInit() {
        this._currentUser = this._storeQuerier.getCurrentUser();
        this._initDialogTitle();
        this._initDualListConfig();
        this._initDualListData();
        this._initSubscriptions();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeunloadHandler($event: any) {
        if (this.dialogOpened) {
            $event.returnValue = true;
        }
    }

    onCancelClick() {
        if (this._disabledCancel) {
            return;
        }
        if (this._isDirty()) {
            const subscription = this._alertWindow.warn(businessConstants.alertWindow.warnTitle, [businessConstants.alertWindow.warnContent])
                .subscribe((result: boolean) => {
                    if (result) {
                        this._onClose();
                    }
                    subscription.unsubscribe();
                });
        } else {
            this._onClose();
        }
    }

    onImportConfigFileClick() {
        document.getElementById('file-selector').click();
    }

    onConfigFileInput(event) {
        if (event.target && event.target.files && event.target.files.length > 0) {
            const fileToLoad = event.target.files[0];
            const fileName = fileToLoad.name;
            // handle the file name which must be json file
            if (this._fileHelperService.isSpecifiedExtensionFile(fileName, '.json')) {
                const fileReader: FileReader = new FileReader();
                fileReader.readAsText(fileToLoad);
                fileReader.onloadend = ((e) => {
                    let workflowConfig = '';
                    try {
                        // handle the file which must complete json
                        workflowConfig = JSON.parse(fileReader.result as string);
                    } catch {
                        this._invalidConfigAlert();
                        return;
                    }
                    // handle the file which is must workflow json
                    if (this._service.isWorkflowJsonFormat(workflowConfig)) {
                        if (workflowConfig) {
                            this._service.getImprotWorkflowJsonByConfiguration(workflowConfig).pipe(
                                catchError(() => {
                                    return of('error');
                                }),
                                take(1)
                            ).subscribe((res) => {
                                if (res && res !== 'error') {
                                    this._workflowConfigService.setProcessDefinitionConfig = res;
                                    const importURL = '/workflow/config/edit';
                                    if (this._router.url === importURL) {
                                        this._workflowDialogService.import$.next();
                                    } else {
                                        this._router.navigate([importURL]);
                                    }
                                } else {
                                    const msg = 'Import workflow failed.';
                                    this._toast.notify({
                                        style: NotificationStyles.Error,
                                        message: msg
                                    });
                                }
                            });
                        }
                    } else {
                        this._invalidConfigAlert();
                    }
                });
            } else {
                this._invalidConfigAlert();
            }
        }
    }

    onPublicChange() {
        this._hadModify = true;
        if (!this.config.isPublic) {
            if (this.config.accessList && this.config.accessList.length === 0) {
                setTimeout(() => {
                    this.dualListInst.setSelectedItem(this._currentUser.id, false);
                });
                this._accessList = [this._currentUser.id];
                this.config.accessList = this._accessList;
            }
        }
    }

    onSaveClick() {
        this._doValidation();
        if (this.contentForm.invalid) {
            return;
        }
        if (!this._isDirty()) {
            this._onClose();
            return;
        }
        this.isSaving = true;
        this._onSave();
    }

    private _invalidConfigAlert(): void {
        const subscription = this._alertWindow.error('Invalid Workflow Configuration',
            ['You are importing an invalid workflow configuration file.']).subscribe((result: boolean) => {
                subscription.unsubscribe();
            });
    }

    private _onClose() {
        this.dialogOpened = false;
        this._resetData();
    }

    private _onSave() {
        const saveData = new WorkflowConfig();
        saveData.name = this.name.value;
        if (this._opType !== WorkflowDialogOperations.NEW) {
            saveData.id = this.config.id;
        }
        this._service.checkDuplicatedName(saveData).pipe(
            catchError(err => {
                if (err.message && err.message.includes('ERR_NAME_EXISTED')) {
                    this.name.setErrors({
                        duplicated: true
                    });
                }
                return of('ERROR');
            }),
            take(1),
        ).subscribe(res => {
            if (res !== 'ERROR') {
                this.config.name = this.name.value;
                this.config.description = this.description.value;
                this.config.isPublic = this.config.isPublic;
                this.config.accessList = this.config.isPublic ? [] : this._accessList;
                this._store.dispatch({
                    type: WorkflowActionTypes.CREATE_WORKFLOW_BRIEF,
                    payload: this.config
                });
                this._workflowDialogService.result$.next({
                    type: 'SAVE',
                    payload: this.config,
                    opType: this._opType
                });
                if (this._opType === WorkflowDialogOperations.NEW) {
                    this._router.navigate(['workflow/config', 'new']);
                } else {
                    this._store.dispatch({
                        type: SystemActionTypes.EDIT_SCREEN,
                        payload: this.config,
                    });
                }
                this._onClose();
            }
            this.isSaving = false;
        });
    }

    private _resetData() {
        this._hadModify = false;
        this.contentForm.reset();
        this.dualListConfig.dataSource.forEach(item => {
            item.direction = 'left';
        });
        this.config = new WorkflowConfig();
    }

    private _initDualListConfig() {
        this.dualListConfig = new DualListConfig();
        this.dualListConfig.titles = [businessConstants.dualList.userNotAllowedAccessTitle, businessConstants.dualList.userAllowedAccessTitle];
        this.dualListConfig.placeholderText = 'Search User or Teams';
        this.dualListConfig.dataTextField = 'name';
        this.dualListConfig.dataValueField = 'id';
        this.dualListConfig.dataSource = [];
        this._destroySubscriptions.push(
            this.dualListConfig.feedbackSubject$.subscribe(data => this._onChangeAccessList(data)),
        );
    }

    private _initDualListData() {
        const accessListSelector = createSelector(
            (state: AppState) => state.users,
            (state: AppState) => state.teams,
            (allUser, allTeam) => {
                if (allUser.length === 0 && allTeam.length === 0) {
                    return [];
                }
                const users = allUser.map(item => {
                    return new DualListItem(item);
                });
                const teams = allTeam.map(item => {
                    return new DualListItem(Object.assign({ type: 'team' }, item));
                });
                return users.concat(teams);
            }
        );
        this._destroySubscriptions.push(
            this._store.pipe(
                select(accessListSelector),
                filter((list: any) => list.length > 0),
            ).subscribe(list => {
                this.dualListConfig.dataSource = list;
            })
        );
    }

    private _onChangeAccessList(event) {
        if (event.type === DualListEventType.selectChange) {
            this._accessList = this.dualListInst.getSelectedItem(true);
        }
    }

    private _initDialogTitle() {
        this.dialogTitle = 'New Workflow';
        if (this._opType === WorkflowDialogOperations.EDIT || this._opType === WorkflowDialogOperations.COPY) {
            this.dialogTitle = 'Edit Workflow Details';
        }
    }

    private _initSubscriptions() {
        this._destroySubscriptions.push(
            this._workflowDialogService.open$.subscribe((config) => {
                if (config.data) {
                    this.config = _lodash.cloneDeep(config.data);
                }
                this._opType = config.opType;
                this.dialogOpened = true;
                this.isSaving = false;
                this._disabledCancel = config.disabledCancel;
                this._initDialogTitle();
                this._accessList = [this._currentUser.id];
                if (this._opType === WorkflowDialogOperations.NEW) {
                    setTimeout(() => {
                        this.dualListInst.setSelectedItem(this._currentUser.id, false);
                    });
                }
                if ((this._opType === WorkflowDialogOperations.EDIT || this._opType === WorkflowDialogOperations.COPY)
                    && this.config) {
                    this._initData();
                }
                const _originalConfig = _lodash.cloneDeep(this.config);
                this._workflowDialogService.edit$.next(_originalConfig);
            }),
            this.dualListConfig.feedbackSubject$.pipe(
                filter((item) => item.type === DualListEventType.selectChange)
            ).subscribe(() => {
                this._hadModify = true;
            }),
            this._workflowDialogService.openFileBrowserDialog$.subscribe((data) => {
                this.onConfigFileInput(data);
            })
        );
    }

    private _doValidation() {
        this.name.setErrors(null);
        this.description.setErrors(null);

        if (!this.name.value) {
            this.name.setErrors({
                required: true
            });
        } else if (this.name.value.length > 128) {
            this.name.setErrors({
                maxlength: true
            });
        }

        if (this.description.value && this.description.value.length > 256) {
            this.description.setErrors({
                maxlength: true
            });
        }
    }

    private _isDirty(): boolean {
        return this.contentForm.dirty || this._hadModify;
    }

    private _initData() {
        this.name.setValue(this.config.name);
        this.description.setValue(this.config.description);
        if (this.config.accessList && this.config.accessList.length > 0) {
            this.dualListConfig.dataSource.forEach((data, index) => {
                if (this.config.accessList.indexOf(data.data.id) !== -1) {
                    data.direction = 'right';
                }
            });
            this._accessList = this.config.accessList;
        }
    }
}

